import { ChevronRightIcon, PlayIcon } from "lucide-react";
import { Dot } from "@/components/app/record";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  liveGameClock,
  openingPitch,
  type PitchPhase,
  pitchLane,
  pitchPhase,
} from "../../../shared/live-view";
import type { RosterProfileLink } from "../../../shared/player-profile";
import type { slotContext } from "../../../shared/standings";
import { parseLocal } from "../../../shared/time";
import { todayLocal } from "../../../shared/tournament";
import type {
  Division,
  Field,
  FieldId,
  GameState,
  Pool,
  ScheduledGame,
  Team,
} from "../../../shared/types";
import { useT } from "../../i18n";
import { dayHeading } from "../../i18n/format";
import { divisionTagOf, gameNoteOf, poolNameOf } from "../../i18n/labels";
import { type LiveGame, useNow } from "../../lib/useGameState";
import { type HeroSide, LiveHero, NextHero } from "./GameHero";
import { LiveStory } from "./LiveStory";
import { LiveSupport } from "./LiveSupport";
import { type BoardSide, type LabelCtx, Section, SideLine, sideOf, Tag, timeOf } from "./parts";

/** The score feed's state, in the words the view's status line says. */
const FEED = {
  connecting: "nav.connecting",
  connected: "nav.connected",
  reconnecting: "nav.reconnecting",
} as const;

/**
 * The board's Live view (2026-09-19, the owner's pick «Ефір + поля» of four
 * mockups): THE PITCHES ARE THE CONTROL. A pill per pitch carries that pitch's
 * score, so every game is on the first screen of a phone; under the pills
 * everything belongs to the chosen pitch - the game on it as a hero, its
 * story, what is next there and what was played there today.
 *
 * What it replaced, measured on the Beach Nationals board that morning: with
 * nothing on, the whole view was one line («Зараз гри немає») under a head
 * that said «Триває»; with two games on, one tracked game's card was 897px of
 * a phone and the second pitch's score started at y = 1239 of 812; on a laptop
 * the shorter of two cards stretched over ~250px of empty glass.
 *
 * A pitch is never blank: a game in play or a final still on the scorekeeper's
 * phone is the hero; otherwise the hero is the next game there with its
 * kickoff and a countdown; with nothing left, one line says so above the
 * day's results.
 */
export function LiveView({
  fields,
  games,
  teams,
  schedule,
  pools,
  divisions,
  ctx,
  labelCtx,
  mine,
  feed,
  pitch,
  onPitch,
  onOpenGame,
  profiles,
}: {
  fields: Field[];
  /** Each pitch's socket, held by the page (one per field for the whole
   *  board, whichever view is open). */
  games: Record<string, LiveGame>;
  teams: Team[];
  schedule: ScheduledGame[];
  pools: Pool[];
  divisions: Division[];
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  /** The viewer's own squads: their game opens first. */
  mine: Team[];
  feed: keyof typeof FEED;
  /** The pitch the URL names; null until the viewer picks one. */
  pitch: FieldId | null;
  onPitch: (field: FieldId) => void;
  onOpenGame: (game: ScheduledGame) => void;
  profiles?: ReadonlyMap<string, RosterProfileLink>;
}) {
  const t = useT();
  const today = todayLocal();
  // A minute's resolution is plenty for "two hours past its start"; the
  // clocks and countdowns under this tick for themselves.
  const now = useNow(0, 60_000);
  const mineIds = new Set(mine.map((team) => team.id));

  const pitches = fields
    .map((field) => {
      const game = games[field.id];
      const state = game?.state ?? null;
      const phase = pitchPhase(state);
      const shown = phase === "idle" ? null : state;
      const lane = pitchLane(schedule, field.id, {
        today,
        now,
        onNow: shown?.scheduleGameId ?? null,
      });
      return { field, game, state: shown, phase, lane };
    })
    // A pitch with no game on it, none ahead and none played today has
    // nothing to say, and a pill for it would be a tab onto an empty page.
    .filter((p) => p.state !== null || p.lane.later.length > 0 || p.lane.earlier.length > 0);

  const opening = openingPitch(
    pitches.map((p) => ({
      fieldId: p.field.id,
      phase: p.phase,
      mine:
        p.state !== null &&
        [p.state.teamA.teamId, p.state.teamB.teamId].some((id) => id !== null && mineIds.has(id)),
      nextStartAt: p.lane.later[0]?.startAt,
    })),
  );
  const selected =
    pitches.find((p) => p.field.id === pitch) ?? pitches.find((p) => p.field.id === opening);

  if (selected === undefined) {
    return (
      <Section title={t("board.onFields")}>
        {/* The way OUT to the stream while there is nothing else to show:
            "where do I watch" is the first question at 08:55. */}
        {fields.some((f) => f.streamUrl !== undefined) && (
          <div className="flex flex-wrap gap-2">
            {fields
              .filter((f) => f.streamUrl !== undefined)
              .map((f) => (
                <Button
                  key={f.id}
                  variant="outline"
                  className="flex-1 basis-40"
                  render={<a href={f.streamUrl} target="_blank" rel="noopener noreferrer" />}
                >
                  <PlayIcon className="text-brand-ink dark:text-brand" />
                  {t("board.watchLive", { field: f.name })}
                </Button>
              ))}
          </div>
        )}
        <Card className="p-4 text-sm text-muted-foreground">{t("board.noGame")}</Card>
      </Section>
    );
  }

  const { field, state, lane } = selected;
  const row =
    state?.scheduleGameId == null ? undefined : schedule.find((g) => g.id === state.scheduleGameId);
  // With one pitch there are no pills, so the hero names the pitch itself.
  const lead = pitches.length > 1 ? [] : [field.name];
  const next = lane.later[0];
  // The next game leads the pane above as the hero while nothing is on, so
  // the list under it starts from the one after.
  const later = state === null ? lane.later.slice(1) : lane.later;
  const laterDay = later[0]?.startAt.slice(0, 10);

  return (
    <section className="space-y-4">
      <h2 className="sr-only">{t("board.onFields")}</h2>
      {pitches.length > 1 && (
        <Tabs
          value={selected.field.id}
          onValueChange={(value) => {
            if (typeof value === "string") onPitch(value);
          }}
        >
          {/* One row that scrolls, never two: past four pitches the pills run
              off the edge rather than stacking a second trough over the hero
              (GLASS.md §9, the admin header's trap). Two or three share a
              phone's width; on a laptop the row hugs its pills. */}
          <TabsList
            aria-label={t("board.live.pitches")}
            className="no-bar flex-nowrap overflow-x-auto adm:w-auto adm:self-start"
          >
            {pitches.map((p) => (
              <TabsTrigger
                key={p.field.id}
                value={p.field.id}
                // Two lines, so the trigger's own height goes: the pitch's name
                // over its score. Still well past the 44px thumb floor.
                className="group/pill h-auto min-w-40 flex-1 px-3 py-2 pointer-coarse:h-auto adm:min-w-60 adm:flex-none [&>span]:flex [&>span]:min-w-0 [&>span]:w-full [&>span]:flex-col [&>span]:items-stretch [&>span]:gap-1.5"
              >
                <PitchPill
                  field={p.field}
                  phase={p.phase}
                  state={p.state}
                  skew={p.game?.skew ?? 0}
                  connected={p.game?.connected === true}
                  next={p.lane.later[0]}
                  today={today}
                  teams={teams}
                />
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {/* Said aloud always, SHOWN only while the score is not arriving: a
          connected feed is the pulsing dot on each pill, and a sentence about
          it under every healthy page was one of the five blocks a phone
          scrolled past before any content. */}
      <span role="status" className={cn("flex", feed === "connected" && "sr-only")}>
        <Dot
          tone={feed === "connected" ? "ok" : feed === "reconnecting" ? "warn" : "idle"}
          className="text-muted-foreground"
          label={`${t("nav.scoreConnection")}: ${t(FEED[feed])}`}
        />
      </span>

      {/* Re-keyed by the pitch, so a pill's tap rises in like a view switch
          does (GLASS.md §7: the one entrance, and it only moves and fades). */}
      <div
        key={field.id}
        className={cn(
          "board-enter animate-in fade-in-0 slide-in-from-bottom-2 grid gap-x-5 gap-y-6 duration-300 adm:items-start",
          (later.length > 0 || lane.earlier.length > 0) &&
            "adm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]",
        )}
      >
        <div className="min-w-0 space-y-3">
          {state !== null ? (
            <>
              <LiveHero
                state={state}
                skew={selected.game?.skew ?? 0}
                connected={selected.game?.connected === true}
                context={[...lead, ...contextOf(row, divisions, pools)]}
                fieldName={field.name}
                streamUrl={field.streamUrl}
              />
              <LiveSupport state={state} connected={selected.game?.connected === true} />
              <LiveStory
                key={`${state.scheduleGameId}:${state.teamA.teamId}:${state.teamB.teamId}`}
                state={state}
                teams={teams}
                schedule={schedule}
                pools={pools}
                divisions={divisions}
                profiles={profiles}
              />
            </>
          ) : next !== undefined ? (
            <NextHero
              a={heroSide(sideOf(next, "a", ctx, labelCtx))}
              b={heroSide(sideOf(next, "b", ctx, labelCtx))}
              startAt={next.startAt}
              today={today}
              context={[...lead, ...contextOf(next, divisions, pools)]}
              fieldName={field.name}
              streamUrl={field.streamUrl}
            />
          ) : (
            <Card className="p-4 text-sm text-muted-foreground">
              {[...lead, t("board.live.pitchDone")].join(" · ")}
            </Card>
          )}
        </div>

        {(later.length > 0 || lane.earlier.length > 0) && (
          <div className="min-w-0 space-y-6">
            {later.length > 0 && (
              <Section
                title={t("board.live.laterHere")}
                // The day, when it is not today: a bare "09:00" on a Saturday
                // evening reads as tonight.
                note={
                  laterDay === undefined || laterDay === today ? undefined : dayHeading(laterDay)
                }
              >
                <Card className="gap-0 divide-y p-0">
                  {later.map((g, i) => (
                    <LaterRow
                      key={g.id}
                      game={g}
                      // Pink marks "next" in a list, and only the one: the
                      // first row, unless the hero above already is it.
                      first={i === 0 && state !== null}
                      today={today}
                      ctx={ctx}
                      labelCtx={labelCtx}
                    />
                  ))}
                </Card>
              </Section>
            )}
            {lane.earlier.length > 0 && (
              <Section title={t("board.live.earlierHere")}>
                <Card className="gap-0 divide-y p-0">
                  {lane.earlier.map((g) => (
                    <EarlierRow
                      key={g.id}
                      game={g}
                      ctx={ctx}
                      labelCtx={labelCtx}
                      onOpen={() => onOpenGame(g)}
                    />
                  ))}
                </Card>
              </Section>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/** What the game IS: the round's own name when the organiser gave one
 *  («Півфінал 2» is what a spectator is looking for), else the division and
 *  the pool. A scratch game with no schedule row says nothing. */
function contextOf(
  game: ScheduledGame | undefined,
  divisions: Division[],
  pools: Pool[],
): string[] {
  if (game === undefined) return [];
  const division = divisionTagOf(divisions, game.divisionId);
  const note = gameNoteOf(game);
  const second = note ?? (game.poolId !== undefined ? poolNameOf(pools, game.poolId) : undefined);
  return [division, second].filter((x): x is string => x !== undefined);
}

function heroSide(side: BoardSide): HeroSide {
  const team = side.team;
  return team === null
    ? { name: side.text }
    : {
        name: team.name,
        short: team.short,
        color: team.color,
        logo: team.logo,
        logoDark: team.logoDark,
      };
}

// ---------- the pills ----------

/**
 * One pitch, as its pill says it: the name over the score and the clock while
 * a game is on, the final's score once it is over, else the next kickoff.
 *
 * The clock ticks once a second here, locally, from the state the pitch's own
 * socket delivered - the same arithmetic as the hero under it, so the two can
 * never disagree by more than a tick.
 */
function PitchPill({
  field,
  phase,
  state,
  skew,
  connected,
  next,
  today,
  teams,
}: {
  field: Field;
  phase: PitchPhase;
  state: GameState | null;
  skew: number;
  connected: boolean;
  next: ScheduledGame | undefined;
  today: string;
  teams: Team[];
}) {
  const t = useT();
  const now = useNow(skew, 1000);
  const muted = "text-muted-foreground group-data-active/pill:text-primary-foreground/70";
  const codeOf = (slot: ScheduledGame["slotA"]): string | undefined =>
    slot?.kind === "team" ? teams.find((team) => team.id === slot.teamId)?.short : undefined;
  const codes = next === undefined ? [] : [codeOf(next.slotA), codeOf(next.slotB)];

  return (
    <>
      <span className="flex items-center justify-between gap-2">
        <span className="truncate text-left text-xs font-medium">{field.name}</span>
        {phase === "live" && (
          <span className="flex shrink-0 items-center gap-1.5">
            {state !== null && (
              <span className={cn("tabular text-xs font-semibold", muted)}>
                {liveGameClock(state, now)}
              </span>
            )}
            <span
              aria-hidden="true"
              className={cn(
                "size-1.5 rounded-full",
                connected ? "motion-safe:animate-pulse bg-go" : "bg-warn",
              )}
            />
          </span>
        )}
      </span>
      {state !== null ? (
        <span className="flex items-center justify-between gap-2">
          <span className={cn("team-code text-xs", muted)}>{state.teamA.short}</span>
          <span className="nxt tabular flex items-center gap-1.5 whitespace-nowrap text-lg leading-none text-foreground group-data-active/pill:text-primary-foreground">
            {state.teamA.score} : {state.teamB.score}
          </span>
          <span className={cn("team-code text-xs", muted)}>{state.teamB.short}</span>
        </span>
      ) : next !== undefined ? (
        <span className="flex min-w-0 items-center justify-between gap-2">
          <span className={cn("min-w-0 whitespace-normal text-left text-xs font-semibold", muted)}>
            {next.startAt.slice(0, 10) === today
              ? codes.every((c) => c !== undefined)
                ? codes.join(" – ")
                : ""
              : dayHeading(next.startAt.slice(0, 10))}
          </span>
          <span className="nxt tabular shrink-0 text-lg leading-none text-foreground group-data-active/pill:text-primary-foreground">
            {timeOf(next.startAt)}
          </span>
        </span>
      ) : (
        <span className={cn("text-left text-xs font-semibold", muted)}>
          {t("board.live.allPlayed")}
        </span>
      )}
    </>
  );
}

// ---------- the pitch's day ----------

/** «за 28 хв», inside the last hour only and only while it is ahead: the front
 *  page's words, so the two surfaces count a kickoff down alike. Hours away
 *  says nothing - «за 8 год 42 хв» does not fit under a time, and a kickoff
 *  that far off is read from the clock beside it. */
function useAhead(): (startAt: string, today: string) => string | undefined {
  const t = useT();
  const now = useNow(0, 30_000);
  return (startAt, today) => {
    const start = parseLocal(startAt);
    if (start === null || startAt.slice(0, 10) !== today) return undefined;
    const mins = Math.round((start.getTime() - now) / 60_000);
    return mins > 0 && mins < 60 ? t("front.next.inMinutes", { n: mins }) : undefined;
  };
}

/** A game still to come on this pitch: when, who, which division. */
function LaterRow({
  game,
  first,
  today,
  ctx,
  labelCtx,
}: {
  game: ScheduledGame;
  first: boolean;
  today: string;
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
}) {
  const ahead = useAhead();
  const note = gameNoteOf(game);
  const division = divisionTagOf(labelCtx.divisions, game.divisionId);
  const soon = first ? ahead(game.startAt, today) : undefined;
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3.5 py-2.5">
      {/* w-16: the widest time `clockOf` can print is `08:08`, 3.71em of NXT
          (README §2), and the countdown under it needs the same room. */}
      <span className="flex w-16 flex-col gap-1">
        <span
          className={cn(
            "nxt tabular text-base leading-none",
            first ? "text-brand-ink dark:text-brand" : "text-muted-foreground",
          )}
        >
          {timeOf(game.startAt)}
        </span>
        {soon !== undefined && (
          <span className="text-[0.6875rem] leading-tight text-muted-foreground">{soon}</span>
        )}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        {note !== undefined && (
          <span className="eyebrow mb-0.5 text-muted-foreground">{note.toUpperCase()}</span>
        )}
        <SideLine side={sideOf(game, "a", ctx, labelCtx)} />
        <SideLine side={sideOf(game, "b", ctx, labelCtx)} />
      </span>
      {division !== undefined && <Tag tone="brand">{division}</Tag>}
    </div>
  );
}

/** A game already played here today: the two sides with their scores, the
 *  loser dimmed as every result row dims it, the row opening the game's page. */
function EarlierRow({
  game,
  ctx,
  labelCtx,
  onOpen,
}: {
  game: ScheduledGame;
  ctx: ReturnType<typeof slotContext>;
  labelCtx: LabelCtx;
  onOpen: () => void;
}) {
  const result = game.result;
  if (result === undefined) return null;
  const a = sideOf(game, "a", ctx, labelCtx);
  const b = sideOf(game, "b", ctx, labelCtx);
  const line = (side: BoardSide, score: number, lost: boolean) => (
    <span className={cn("flex items-center justify-between gap-3", lost && "opacity-60")}>
      <SideLine side={side} />
      <span className="nxt tabular shrink-0 text-base leading-none">{score}</span>
    </span>
  );
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${a.text} ${result.scoreA} – ${result.scoreB} ${b.text}`}
      className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-accent"
    >
      <span className="nxt tabular w-16 text-base leading-none text-muted-foreground">
        {timeOf(game.startAt)}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        {line(a, result.scoreA, result.scoreA < result.scoreB)}
        {line(b, result.scoreB, result.scoreB < result.scoreA)}
      </span>
      <ChevronRightIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground/60" />
    </button>
  );
}
